(function () {
  if (window.customElements.get('bundle-picker')) return;

  function formatMoney(cents) {
    const format = (window.themeVariables && window.themeVariables.settings.moneyFormat) || '${{amount}}';
    const value = cents / 100;
    const withThousands = (str, sep) => str.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    const formatters = {
      amount: () => withThousands(value.toFixed(2), ','),
      amount_no_decimals: () => withThousands(Math.round(value).toString(), ','),
      amount_with_comma_separator: () => withThousands(value.toFixed(2), '.').replace(/\.(\d{2})$/, ',$1'),
      amount_no_decimals_with_comma_separator: () => withThousands(Math.round(value).toString(), '.'),
      amount_with_space_separator: () => withThousands(value.toFixed(2), ' ').replace(/ (\d{2})$/, ',$1'),
      amount_no_decimals_with_space_separator: () => withThousands(Math.round(value).toString(), ' '),
      amount_with_apostrophe_separator: () => withThousands(value.toFixed(2), "'"),
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => (formatters[key] || (() => ''))());
  }

  class BundlePicker extends HTMLElement {
    connectedCallback() {
      const variantsScript = this.querySelector('[data-bundle-variants]');
      this._variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
      this._slotTemplate = this.querySelector('[data-bundle-slot-template]');
      this._slotsContainer = this.querySelector('[data-bundle-slots]');
      this._tierButtons = Array.from(this.querySelectorAll('[data-bundle-tier]'));
      this._formId = this.getAttribute('form-id');
      this._form = document.getElementById(this._formId);
      this._distinctRequired = this.hasAttribute('distinct-required');
      this._discountPrefix = this.getAttribute('discount-code-prefix') || 'BUNDLE';
      this._packPropertyLabel = (this.getAttribute('pack-property-label') || '').trim();

      this._uid = Math.random().toString(36).slice(2, 8);
      this._activeTier = null;
      // Pristine ATC label stamped by Liquid — safe across re-inits (customizer section rendering).
      this._atcBaseLabel = this.getAttribute('atc-base-label') || 'Add to cart';
      const btnOnInit = this._submitButton();
      // If the button already got mutated by a previous picker instance (customizer re-init while a tier was active),
      // reset it to the pristine label so we never carry stale price text into the "unselected" state.
      if (btnOnInit && btnOnInit.querySelector('[data-bundle-atc-content]')) {
        btnOnInit.textContent = this._atcBaseLabel;
      }

      this._tierButtons.forEach((btn) => btn.addEventListener('click', (e) => this._onTierClick(e)));
      this._slotsContainer?.addEventListener('change', (e) => this._onSlotChange(e));
      this._form?.addEventListener('submit', (e) => this._onFormSubmit(e), { capture: true });

      this._currentTopVariant = null;
      this._form?.addEventListener('variant:change', (e) => {
        if (e.detail?.variant) this._currentTopVariant = e.detail.variant;
        if (this._activeTier?.mode === 'pack') this._updateTotal();
      });
    }

    _restoreSubmitLabel() {
      const btn = this._submitButton();
      if (btn) btn.textContent = this._atcBaseLabel;
    }

    _getTopVariant() {
      if (this._currentTopVariant) {
        return this._resolveVariantById(this._currentTopVariant.id) || this._currentTopVariant;
      }
      const dataScript = document.querySelector(`variant-picker[form-id="${this._formId}"] script[data-variant]`);
      if (dataScript) {
        try {
          const parsed = JSON.parse(dataScript.textContent);
          if (parsed && parsed.id) return this._resolveVariantById(parsed.id) || parsed;
        } catch (e) {}
      }
      const idInput = this._form?.querySelector('input[name="id"]');
      if (idInput && idInput.value) return this._resolveVariantById(parseInt(idInput.value, 10));
      return this._variants[0];
    }

    _onTierClick(event) {
      const btn = event.currentTarget;
      const alreadySelected = btn.classList.contains('is-selected');

      this._tierButtons.forEach((b) => {
        b.classList.remove('is-selected');
        b.setAttribute('aria-selected', 'false');
      });

      if (alreadySelected) {
        this._activeTier = null;
        if (this._slotsContainer) {
          this._slotsContainer.innerHTML = '';
          this._slotsContainer.hidden = true;
        }
        this._restoreSubmitLabel();
        if (this._submitButton()) this._submitButton().disabled = false;
        return;
      }

      btn.classList.add('is-selected');
      btn.setAttribute('aria-selected', 'true');
      this._activeTier = {
        key: btn.dataset.tierKey,
        count: parseInt(btn.dataset.tierCount, 10),
        discount: parseFloat(btn.dataset.tierDiscount) || 0,
        mode: btn.dataset.tierMode === 'pack' ? 'pack' : 'mix_and_match',
      };

      if (this._activeTier.mode === 'mix_and_match') {
        this._slotsContainer.hidden = false;
        this._renderSlots();
      } else {
        this._slotsContainer.hidden = true;
        this._slotsContainer.innerHTML = '';
      }
      this._updateTotal();
    }

    _renderSlots() {
      if (!this._activeTier || !this._slotTemplate) return;
      this._slotsContainer.innerHTML = '';
      const topVariant = this._getTopVariant();
      for (let i = 0; i < this._activeTier.count; i++) {
        const fragment = this._slotTemplate.content.cloneNode(true);
        const slot = fragment.querySelector('[data-bundle-slot-index]');
        if (!slot) continue;
        slot.setAttribute('data-bundle-slot-index', String(i));
        const slotUid = `bundle-slot-${this._uid}-${i}`;
        slot.querySelectorAll('input[data-bundle-option-input]').forEach((input, idx) => {
          const oldId = input.id;
          const newId = `${slotUid}-${idx}`;
          input.id = newId;
          input.name = input.name.replace(/bundle\[\d+\]/, `bundle[${i}]`);
          const linkedLabel = slot.querySelector(`label[for="${(window.CSS && CSS.escape) ? CSS.escape(oldId) : oldId}"]`);
          if (linkedLabel) linkedLabel.htmlFor = newId;
        });
        if (topVariant) this._selectVariantInSlot(slot, topVariant);
        this._slotsContainer.appendChild(slot);
        if (i < this._activeTier.count - 1) {
          const hr = document.createElement('hr');
          hr.className = 'bundle-picker__slot-divider';
          this._slotsContainer.appendChild(hr);
        }
      }
      this._updateAllSlotTitles();
    }

    _selectVariantInSlot(slotEl, variant) {
      if (!variant || !variant.options) return;
      variant.options.forEach((value, idx) => {
        const position = idx + 1;
        const input = slotEl.querySelector(
          `input[data-option-position="${position}"][value="${(window.CSS && CSS.escape) ? CSS.escape(value) : value}"]`
        );
        if (input) input.checked = true;
      });
    }

    _readSlotOptions(slotEl) {
      const values = [];
      slotEl.querySelectorAll('[data-bundle-option-position]').forEach((fieldset) => {
        const position = parseInt(fieldset.dataset.bundleOptionPosition, 10);
        const checked = fieldset.querySelector('input[data-bundle-option-input]:checked');
        if (checked) values[position - 1] = checked.value;
      });
      return values;
    }

    _resolveVariantForSlot(slotEl) {
      const values = this._readSlotOptions(slotEl);
      if (!values.length) return null;
      return this._variants.find(
        (v) => v.options && v.options.length === values.length && v.options.every((opt, i) => opt === values[i])
      );
    }

    _resolveVariantById(id) {
      return this._variants.find((v) => v.id === id);
    }

    _updateAllSlotTitles() {
      this._slotsContainer.querySelectorAll('[data-bundle-slot-index]').forEach((slot) => {
        const variant = this._resolveVariantForSlot(slot);
        const titleEl = slot.querySelector('[data-bundle-slot-variant-title]');
        if (!titleEl) return;
        if (!variant) {
          titleEl.textContent = '—';
          slot.classList.add('is-invalid');
        } else {
          titleEl.textContent = variant.available ? variant.title : `${variant.title} · Out of stock`;
          slot.classList.toggle('is-invalid', !variant.available);
        }
      });
    }

    _onSlotChange(event) {
      if (!event.target.matches('[data-bundle-option-input]')) return;
      this._updateAllSlotTitles();
      this._updateTotal();
    }

    _collectSlots() {
      return Array.from(this._slotsContainer.querySelectorAll('[data-bundle-slot-index]')).map((slotEl) => ({
        slotEl,
        variant: this._resolveVariantForSlot(slotEl),
      }));
    }

    _updateTotal() {
      if (!this._activeTier) return;

      let subtotal = 0;
      let allValid = true;
      if (this._activeTier.mode === 'pack') {
        const top = this._getTopVariant();
        if (!top || !top.available) allValid = false;
        subtotal = (top ? top.price : 0) * this._activeTier.count;
      } else {
        const slots = this._collectSlots();
        subtotal = slots.reduce((sum, s) => sum + (s.variant ? s.variant.price : 0), 0);
        allValid = slots.every((s) => s.variant && s.variant.available);
      }
      const discounted = Math.round((subtotal * (100 - this._activeTier.discount)) / 100);

      const btn = this._submitButton();
      if (btn) {
        btn.innerHTML = `<span data-bundle-atc-content style="color: #fff;">${this._atcBaseLabel} • ${formatMoney(discounted)}</span>`;
        btn.disabled = !allValid;
        btn.toggleAttribute('data-bundle-invalid', !allValid);
      }
    }

    _submitButton() {
      return this._form?.querySelector('[type="submit"]');
    }

    async _onFormSubmit(event) {
      if (!this._activeTier) return;
      event.preventDefault();
      event.stopImmediatePropagation();

      let items;
      if (this._activeTier.mode === 'pack') {
        const top = this._getTopVariant();
        if (!top || !top.available) return this._flashError('This variant is unavailable.');
        const tierLabel = `${this._activeTier.count}x`;
        const packItem = { id: top.id, quantity: this._activeTier.count };
        if (this._packPropertyLabel) {
          packItem.properties = { [this._packPropertyLabel]: tierLabel };
        }
        items = [packItem];
      } else {
        const slots = this._collectSlots();
        if (slots.some((s) => !s.variant || !s.variant.available)) {
          return this._flashError('Please choose an available option in every slot.');
        }
        if (this._distinctRequired) {
          const ids = slots.map((s) => s.variant.id);
          if (new Set(ids).size !== ids.length) {
            return this._flashError('Each slot must be a different variant.');
          }
        }
        const bundleId = `bundle-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        items = slots.map((s, i) => ({
          id: s.variant.id,
          quantity: 1,
          properties: {
            _bundle_id: bundleId,
            _bundle_tier: this._activeTier.key,
            _bundle_position: String(i + 1),
          },
        }));
      }

      const submitBtn = this._submitButton();
      submitBtn?.setAttribute('aria-busy', 'true');

      try {
        // Ask the theme what sections to bundle in the cart/add.js response,
        // matching what the theme's own ProductForm does so cart drawer/notification stay in sync.
        const sectionsToBundle = ['variant-added'];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:prepare-bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle },
          })
        );

        const firstVariantId = items[0].id;
        const addRes = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items,
            sections: sectionsToBundle.join(','),
            sections_url: `${window.Shopify.routes.root}variants/${firstVariantId}`,
          }),
        });
        const addData = await addRes.json();
        if (!addRes.ok) throw new Error(addData.description || addData.message || 'Add to cart failed');

        if (this._activeTier.discount > 0) {
          const code = `${this._discountPrefix}${this._activeTier.key.toUpperCase()}`;
          window.location.href = `${window.Shopify.routes.root}discount/${encodeURIComponent(code)}?redirect=/cart`;
          return;
        }

        if (
          window.themeVariables?.settings?.cartType === 'page' ||
          window.themeVariables?.settings?.pageType === 'cart'
        ) {
          window.location.href = `${window.Shopify.routes.root}cart`;
          return;
        }

        const cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
        cart.sections = addData.sections;
        const detailItems = addData.items || [addData];
        this._form.dispatchEvent(
          new CustomEvent('variant:add', { bubbles: true, detail: { items: detailItems, cart } })
        );
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', cart },
          })
        );
        submitBtn?.removeAttribute('aria-busy');
      } catch (err) {
        submitBtn?.removeAttribute('aria-busy');
        this._flashError(err.message);
      }
    }

    _flashError(msg) {
      this._form?.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: msg } }));
    }
  }

  customElements.define('bundle-picker', BundlePicker);
})();
